import React, { useEffect, useRef, useState, memo, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getGeoLocationLatLong } from "../../services/commonServices";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const MapBoxComponent = ({
  center = ["-123.2259181", "44.0431493"],
  zoom = 10,
  radiusMiles,
  height = "400px",
  width = "100%",
  onRadiusUpdate,
  handleItemSelection,
  polygonCoordinates,
  showPolygon = false,
  isEditable = false,
  getDetailedAddress = false,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const initialCenter = useMemo(() => center.map(Number), [center]);
  const [currentCenter, setCurrentCenter] = useState(initialCenter);

  const radiusMeters = useMemo(
    () => (radiusMiles ? radiusMiles * 1609.34 : 0),
    [radiusMiles],
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: currentCenter,
      zoom,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    mapRef.current = map;

    map.on("load", () => {
      if (showPolygon && polygonCoordinates.length > 0) {
        drawPolygon(map);
      } else {
        initializeMarker(map);
      }
    });

    return () => map.remove();
  }, []);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: currentCenter,
      zoom,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    mapRef.current = map;
    map.on("load", () => {
      if (showPolygon && mapRef.current?.isStyleLoaded()) {
        drawPolygon(mapRef.current);
      }
    });
  }, [showPolygon, polygonCoordinates]);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: currentCenter,
      zoom,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    mapRef.current = map;
    map.on("load", () => {
      if (!showPolygon && mapRef.current?.isStyleLoaded()) {
        initializeMarker(mapRef.current);
      }
    });
  }, [currentCenter, radiusMiles]);

  useEffect(() => {
    const newCenter = center.map(Number);
    if (!showPolygon) {
      if (
        newCenter[0] !== currentCenter[0] ||
        newCenter[1] !== currentCenter[1]
      ) {
        setCurrentCenter(newCenter);
      }
    }
  }, [center, showPolygon]);

  const initializeMarker = (map) => {
    const marker = new mapboxgl.Marker({ draggable: isEditable ? true : false })
      .setLngLat(currentCenter)
      .addTo(map);

    markerRef.current = marker;
    if (isEditable) {
      marker.on("dragend", async () => {
        const lngLat = marker.getLngLat();
        setCurrentCenter([lngLat.lng, lngLat.lat]);
        const { addressObject } = await getGeoLocationLatLong(
          lngLat.lat,
          lngLat.lng,
          getDetailedAddress,
        );
        handleItemSelection(addressObject);

        if (radiusMiles) {
          addOrUpdateCircle(map);
          fitCircleToMapBounds(map, radiusMeters);
        }
      });
    }

    if (radiusMiles) {
      addOrUpdateCircle(map);
      fitCircleToMapBounds(map, radiusMeters);
    }
  };

  const addOrUpdateCircle = (map) => {
    if (!map.isStyleLoaded()) return;

    const circleSource = map.getSource("circle");
    const circleData = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: currentCenter,
      },
    };

    if (circleSource) {
      circleSource.setData(circleData);
    } else {
      map.addSource("circle", { type: "geojson", data: circleData });

      map.addLayer({
        id: "circle-layer",
        type: "circle",
        source: "circle",
        paint: {
          "circle-radius": {
            stops: [
              [0, 0],
              [20, radiusMeters / 0.075],
            ],
            base: 2,
          },
          "circle-color": "rgba(0, 123, 255, 0.1)",
          "circle-stroke-color": "#007bff",
          "circle-stroke-width": 2,
        },
      });
    }

    if (onRadiusUpdate) onRadiusUpdate(radiusMiles);
  };
  const drawPolygon = (map) => {
    if (!map.isStyleLoaded()) return;
    const formattedCoordinates = polygonCoordinates.map((polygon) =>
      polygon[0].map((point) => [point.lng, point.lat]),
    );

    const polygonSource = map.getSource("polygon");
    const polygonData = {
      type: "FeatureCollection",
      features: formattedCoordinates.map((coordinates) => ({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [coordinates],
        },
      })),
    };

    if (polygonSource) {
      polygonSource.setData(polygonData);
    } else {
      map.addSource("polygon", { type: "geojson", data: polygonData });

      map.addLayer({
        id: "polygon-layer",
        type: "fill",
        source: "polygon",
        paint: {
          "fill-color": "rgba(0, 123, 255, 0.5)",
          "fill-opacity": 0.4,
        },
      });

      map.addLayer({
        id: "polygon-outline-layer",
        type: "line",
        source: "polygon",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
        },
      });
    }
    fitPolygonToBounds(map, formattedCoordinates);
  };

  const fitPolygonToBounds = (map, formattedCoordinates) => {
    const bounds = new mapboxgl.LngLatBounds();

    formattedCoordinates.forEach((coordinates) => {
      coordinates.forEach(([lng, lat]) => bounds.extend([lng, lat]));
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 50, duration: 1000 });
    }
  };

  const fitCircleToMapBounds = (map, radiusMeters) => {
    const bounds = [
      [
        currentCenter[0] - radiusMeters / 111320,
        currentCenter[1] - radiusMeters / 111320,
      ],
      [
        currentCenter[0] + radiusMeters / 111320,
        currentCenter[1] + radiusMeters / 111320,
      ],
    ];

    map.fitBounds(bounds, { padding: 50, duration: 1000 });
  };

  const handleReset = () => {
    if (!mapRef.current) return;

    if (showPolygon && polygonCoordinates.length > 0) {
      const formattedCoordinates = polygonCoordinates.map((polygon) =>
        polygon[0].map((point) => [point.lng, point.lat]),
      );
      fitPolygonToBounds(mapRef.current, formattedCoordinates);
    } else if (radiusMiles) {
      fitCircleToMapBounds(mapRef.current, radiusMeters);
    } else {
      mapRef.current.flyTo({ center: currentCenter, zoom });
    }
  };

  return (
    <div
      style={{ position: "relative", height, width, overflow: "hidden" }}
      id="mapboxId"
      className="h-100"
    >
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
        }}
      ></div>
      <button
        onClick={handleReset}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          padding: "8px 12px",
          backgroundColor: "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
        id="mapboxResetButton"
      >
        Reset Map
      </button>
    </div>
  );
};

export default memo(MapBoxComponent);
